import React, { useState, useEffect, Suspense } from 'react';
import ReactDOM from 'react-dom';

// Storage for external layout configurations
const externalLayouts = new Map();

// Storage for loaded assets to prevent duplicate loading
const loadedAssets = {
  css: new Set(),
  js: new Set(),
  umd: new Set()
};

/**
 * Register an external layout configuration
 * @param {import('../types/external-skin').ExternalLayoutConfig} config 
 */
export function registerExternalLayout(config) {
  if (!config.id || !config.umdUrl || !config.globalName) {
    throw new Error('External layout must have id, umdUrl, and globalName');
  }
  
  externalLayouts.set(config.id, {
    ...config,
    loaded: false,
    loading: false,
    component: null,
    error: null
  });
  
}

/**
 * Unregister an external layout
 * @param {string} layoutId 
 */
export function unregisterExternalLayout(layoutId) {
  externalLayouts.delete(layoutId);
}

/**
 * Get all registered external layouts
 * @returns {Array<import('../types/external-skin').ExternalLayoutConfig>}
 */
export function getExternalLayouts() {
  return Array.from(externalLayouts.values()).map(layout => ({
    id: layout.id,
    name: layout.name,
    description: layout.description,
    version: layout.version,
    author: layout.author,
    preview: layout.preview,
    loaded: layout.loaded,
    error: layout.error
  }));
}

/**
 * Check if a layout is external
 * @param {string} layoutId 
 * @returns {boolean}
 */
export function isExternalLayout(layoutId) {
  return externalLayouts.has(layoutId);
}

/**
 * Load a CSS file dynamically
 * @param {string} url 
 * @returns {Promise<void>}
 */
async function loadCssFile(url) {
  if (loadedAssets.css.has(url)) {
    return;
  }
  
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    // Add crossorigin attribute for CORS
    link.crossOrigin = 'anonymous';
    
    link.onload = () => {
      loadedAssets.css.add(url);
      resolve();
    };
    
    link.onerror = (error) => {
      console.error(`CSS load error for ${url}:`, error);
      // Instead of failing completely, log the error and continue
      // CSS might not be critical for functionality
      loadedAssets.css.add(url); // Mark as loaded to avoid retry
      resolve(); // Resolve instead of reject
    };
    
    document.head.appendChild(link);
  });
}

/**
 * Load a JS file dynamically
 * @param {string} url 
 * @returns {Promise<void>}
 */
async function loadJsFile(url) {
  if (loadedAssets.js.has(url)) {
    return;
  }
  
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    
    script.onload = () => {
      loadedAssets.js.add(url);
      resolve();
    };
    
    script.onerror = () => {
      reject(new Error(`Failed to load JS: ${url}`));
    };
    
    document.body.appendChild(script);
  });
}

/**
 * Load UMD script and get the exported component
 * @param {string} url 
 * @param {string} globalName 
 * @returns {Promise<React.ComponentType>}
 */
async function loadUMDScript(url, globalName) {
  // 레지스트리 스킨의 경우 캐싱 스킵 (독립성 보장)
  // 레지스트리 스킨 URL 패턴들 - 더 포괄적인 패턴으로 수정
  const isRegistrySkin = url.includes('cdn.withcookie-refrigerator.com') || 
                         url.includes('withcookie-bucket.s3.ap-northeast-2.amazonaws.com/skins/') ||
                         url.includes('withcookie-bucket.s3.ap-northeast-2.amazonaws.com/skin/') ||
                         url.includes('/skins/UNKNOWN/') ||
                         url.includes('/skins/USER/') ||
                         url.includes('/skin/sample_');
  

  if (!isRegistrySkin) {
    // Check if already loaded (일반 외부 스킨만)
    if (window[globalName]) {
      return window[globalName];
    }
    
    // Check if already loading (일반 외부 스킨만)
    if (loadedAssets.umd.has(url)) {
      // Wait for it to load
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (window[globalName]) {
            clearInterval(checkInterval);
            resolve(window[globalName]);
          }
        }, 100);
      });
    }
  } else {
    // 레지스트리 스킨의 경우 기존 전역 변수 정리
    if (window[globalName]) {
      // 기존 전역 변수를 삭제하지 말고 새로운 것으로 덮어쓰도록 함
    }
  }
  
  // 레지스트리 스킨이 아닌 경우만 로딩 에셋에 추가
  if (!isRegistrySkin) {
    loadedAssets.umd.add(url);
  }
  
  // Ensure React and ReactDOM are available globally for UMD modules
  if (!window.React) {
    window.React = React;
  }
  if (!window.ReactDOM) {
    window.ReactDOM = ReactDOM;
  }
  
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    
    // 레지스트리 스킨의 경우 캐시 무효화를 위해 타임스탬프 추가
    if (isRegistrySkin) {
      const separator = url.includes('?') ? '&' : '?';
      script.src = `${url}${separator}_t=${Date.now()}`;
    } else {
      script.src = url;
    }
    
    // Try different CORS strategies
    try {
      // First try without CORS headers
      script.crossOrigin = '';
    } catch (e) {
    }
    
    // Set type to module if it might help
    script.type = 'text/javascript';
    // 브라우저 최적화 힌트
    script.async = true;
    script.defer = false;
    
    script.onload = () => {

      // Check for multiple possible global name variations
      const possibleNames = [
        globalName, // 주요 이름 (ExternalSkin{ID})
        globalName.charAt(0).toLowerCase() + globalName.slice(1),
        globalName.charAt(0).toUpperCase() + globalName.slice(1),
        `${globalName}Skin`,
        `${globalName}Layout`,
        `${globalName}Component`,
        // 추가적인 패턴들
        globalName.replace('Skin', ''),
        globalName.replace('Layout', ''),
        globalName.replace('Component', '')
      ];
      

      // 컴포넌트 찾기 함수
      const findComponent = () => {

        // 스크립트 로드 후 전역 변수 스냅샷 (레지스트리 스킨은 모든 변수 확인)
        const afterGlobals = isRegistrySkin ? 
          Object.keys(window) :  // 레지스트리 스킨은 모든 전역 변수 확인
          Object.keys(window).filter(key => 
            key.toLowerCase().includes('modern') || 
            key.toLowerCase().includes('clean') ||
            key.toLowerCase().includes('skin') ||
            key.toLowerCase().includes('layout') ||
            key.toLowerCase().includes('theme')
          );
        

        // 새로 추가된 전역 변수 찾기
        const newGlobals = afterGlobals.filter(key => !script.beforeGlobals.includes(key));

        let foundComponent = null;
        let foundName = null;
        
        // 1. 먼저 예상된 이름들로 시도
        for (const name of possibleNames) {
          if (window[name]) {
            foundComponent = window[name];
            foundName = name;
            break;
          }
        }
        
        // 2. 예상 이름으로 찾지 못했으면 동적 스캔으로 실제 변수 찾기
        if (!foundComponent && newGlobals.length > 0) {
          let actualGlobalName = null;
          let actualComponent = null;
          
          for (const newGlobal of newGlobals) {
            const globalValue = window[newGlobal];

            if (typeof globalValue === 'function') {
              actualGlobalName = newGlobal;
              actualComponent = globalValue;
              break;
            } else if (globalValue && typeof globalValue === 'object') {
              // ES6 모듈 패턴 확인
              const patterns = ['default', 'component', 'Component', 'Layout', 'layout'];
              for (const pattern of patterns) {
                if (globalValue[pattern] && typeof globalValue[pattern] === 'function') {
                  actualGlobalName = newGlobal;
                  actualComponent = globalValue[pattern];
                  break;
                }
              }
              if (actualComponent) break;
            }
          }
          
          // 레지스트리 스킨의 경우 충돌 방지를 위한 처리
          if (actualComponent && isRegistrySkin) {

            // 1. 컴포넌트 추출
            foundComponent = actualComponent;
            foundName = globalName; // ExternalSkin{ID} 사용
            
            // 2. 원본 전역 변수 정리 (충돌 방지)
            try {
              delete window[actualGlobalName];
            } catch (e) {
              console.warn(`⚠️  원본 전역 변수 삭제 실패: ${actualGlobalName}`, e);
            }
            
            // 3. 통일된 이름으로 할당
            window[globalName] = foundComponent;

          } else if (actualComponent) {
            // 일반 외부 스킨은 기존 방식 유지
            foundComponent = actualComponent;
            foundName = actualGlobalName;
          }
        }
        
        return { foundComponent, foundName };
      };
      
      // 컴포넌트 처리 함수
      const processComponent = (foundComponent, foundName) => {
        if (!foundComponent) return false;
        

        // Check if it's a valid React component
        if (typeof foundComponent === 'function') {
          resolve(foundComponent);
          return true;
        } else if (foundComponent && typeof foundComponent === 'object') {
          // If it's an object, check for common export patterns
          const patterns = ['default', 'component', 'Component', 'Layout', 'layout'];
          let actualComponent = null;
          

          for (const pattern of patterns) {
            if (foundComponent[pattern] && typeof foundComponent[pattern] === 'function') {
              actualComponent = foundComponent[pattern];
              break;
            }
          }
          
          // ES6 Module 객체 특별 처리 - __esModule이 true인 경우
          if (!actualComponent && foundComponent.__esModule) {
            // 모든 속성 검사
            for (const key in foundComponent) {
              if (key !== '__esModule' && key !== 'Symbol(Symbol.toStringTag)') {
                const value = foundComponent[key];
                if (typeof value === 'function') {
                  actualComponent = value;
                  break;
                }
              }
            }
          }
          
          if (actualComponent) {
            resolve(actualComponent);
            return true;
          } else {
            reject(new Error(`${foundName} found but no valid React component in object`));
            return true;
          }
        } else {
          reject(new Error(`${foundName} is not a valid React component (type: ${typeof foundComponent})`));
          return true;
        }
      };
      
      // 즉시 확인 + 스마트 폴링
      const attemptLoad = () => {
        const { foundComponent, foundName } = findComponent();
        if (processComponent(foundComponent, foundName)) return true;
        return false;
      };
      
      // 1단계: 즉시 확인 (0ms)
      if (attemptLoad()) return;
      
      // 2단계: 매우 빠른 폴링 (1ms 간격, 최대 20회 = 20ms)
      let attempts = 0;
      const maxAttempts = 20;
      
      const poll = () => {
        if (attemptLoad()) return;
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1); // 1ms 간격으로 단축
        } else {
          console.error('No suitable global variable found. Available globals:', 
            Object.keys(window).filter(key => 
              key.toLowerCase().includes('skin') || 
              key.toLowerCase().includes('modern') ||
              key.toLowerCase().includes('clean')
            )
          );
          reject(new Error(`No suitable global variable found after loading ${url} (tried ${maxAttempts} times)`));
        }
      };
      
      // 즉시 시작 대신 requestAnimationFrame 사용 (더 빠름)
      requestAnimationFrame(poll);
    };
    
    script.onerror = (event) => {
      console.error('Script load error details:', {
        url,
        event,
        message: event.message || 'Unknown error',
        type: event.type,
        target: event.target
      });
      
      // Try loading without CORS as fallback
      if (script.crossOrigin !== undefined) {
        if (!isRegistrySkin) {
          loadedAssets.umd.delete(url);
        }
        
        const fallbackScript = document.createElement('script');
        fallbackScript.src = isRegistrySkin ? script.src : url; // 레지스트리 스킨의 경우 캐시 무효화된 URL 사용
        fallbackScript.type = 'text/javascript';
        
        fallbackScript.onload = script.onload;
        fallbackScript.onerror = () => {
          if (!isRegistrySkin) {
            loadedAssets.umd.delete(url);
          }
          reject(new Error(`Failed to load UMD script (even without CORS): ${url}`));
        };
        
        document.body.appendChild(fallbackScript);
        return;
      }
      
      if (!isRegistrySkin) {
        loadedAssets.umd.delete(url);
      }
      reject(new Error(`Failed to load UMD script: ${url}`));
    };
    

    // 스크립트 로드 전 전역 변수 스냅샷 (레지스트리 스킨은 모든 변수)
    const beforeGlobals = isRegistrySkin ?
      Object.keys(window) :  // 레지스트리 스킨은 모든 전역 변수 스냅샷
      Object.keys(window).filter(key => 
        key.toLowerCase().includes('modern') || 
        key.toLowerCase().includes('clean') ||
        key.toLowerCase().includes('skin') ||
        key.toLowerCase().includes('layout') ||
        key.toLowerCase().includes('theme')
      );

    // 스크립트에 로드 후 콜백에서 사용할 수 있도록 저장
    script.beforeGlobals = beforeGlobals;
    
    document.body.appendChild(script);
  });
}

/**
 * Load an external layout by ID
 * @param {string} layoutId 
 * @returns {Promise<React.ComponentType>}
 */
export async function loadExternalLayout(layoutId) {
  const config = externalLayouts.get(layoutId);
  
  if (!config) {
    throw new Error(`External layout "${layoutId}" not found`);
  }
  
  // Return cached component if already loaded
  if (config.loaded && config.component) {
    return config.component;
  }
  
  // Prevent concurrent loading
  if (config.loading) {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (config.loaded && config.component) {
          clearInterval(checkInterval);
          resolve(config.component);
        } else if (config.error) {
          clearInterval(checkInterval);
          reject(config.error);
        }
      }, 100);
    });
  }
  
  config.loading = true;
  
  try {
    // Load CSS files in parallel (but don't fail if CSS fails)
    if (config.cssUrls && config.cssUrls.length > 0) {
      try {
        await Promise.all(config.cssUrls.map(url => loadCssFile(url)));
      } catch (cssError) {
        console.warn('CSS loading failed, but continuing:', cssError);
        // Continue without CSS - the component might still work
      }
    }
    
    // Load JS files in sequence (to maintain order)
    if (config.jsUrls && config.jsUrls.length > 0) {
      for (const url of config.jsUrls) {
        await loadJsFile(url);
      }
    }
    
    // Load the main UMD bundle
    const Component = await loadUMDScript(config.umdUrl, config.globalName);
    
    config.component = Component;
    config.loaded = true;
    config.loading = false;
    

    return Component;
  } catch (error) {
    config.error = error;
    config.loading = false;
    console.error(`Failed to load external layout "${config.name}":`, error);
    throw error;
  }
}

/**
 * Create a wrapper component that loads external layout on demand
 * @param {string} layoutId 
 * @returns {React.ComponentType}
 */
export function createExternalLayoutWrapper(layoutId) {
  return function ExternalLayoutWrapper(props) {
    const [Component, setComponent] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showLoading, setShowLoading] = useState(false);
    
    useEffect(() => {
      let mounted = true;
      
      // Reset state when layoutId changes
      setComponent(null);
      setError(null);
      setLoading(true);
      setShowLoading(false);
      

      // 20ms 후에만 로딩 메시지 표시 (빠른 로딩 시 깜빡임 방지)
      const loadingTimer = setTimeout(() => {
        if (mounted && loading) {
          setShowLoading(true);
        }
      }, 20);
      
      loadExternalLayout(layoutId)
        .then(LoadedComponent => {

          if (mounted) {
            // Validate component before setting
            if (typeof LoadedComponent === 'function') {
              setComponent(() => LoadedComponent);
              setLoading(false);
              clearTimeout(loadingTimer);
            } else {
              console.error('ExternalLayoutWrapper: 유효하지 않은 컴포넌트 타입:', typeof LoadedComponent);
              setError(new Error(`Invalid component type: ${typeof LoadedComponent}`));
              setLoading(false);
              clearTimeout(loadingTimer);
            }
          }
        })
        .catch(err => {
          console.error('ExternalLayoutWrapper: 컴포넌트 로드 실패:', err);
          if (mounted) {
            setError(err);
            setLoading(false);
            clearTimeout(loadingTimer);
          }
        });
      
      return () => {
        mounted = false;
        clearTimeout(loadingTimer);
      };
    }, [layoutId]); // layoutId를 dependency에 추가
    
    if (loading && showLoading) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column'
        }}>
          <div style={{ marginBottom: '20px' }}>외부 스킨 로딩 중...</div>
        </div>
      );
    }
    
    // 로딩 중이지만 아직 로딩 메시지를 표시하지 않는 상태에서는 null 반환
    if (loading) {
      return null;
    }
    
    if (error) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          color: 'red'
        }}>
          <div>외부 스킨 로드 실패</div>
          <div style={{ fontSize: '14px', marginTop: '10px' }}>{error.message}</div>
        </div>
      );
    }
    
    if (!Component) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column'
        }}>
          <div>컴포넌트 준비 중...</div>
        </div>
      );
    }
    
    // Final validation before render
    if (typeof Component !== 'function') {
      console.error('ExternalLayoutWrapper: 렌더링 직전 컴포넌트 타입 오류:', typeof Component);
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          color: 'red'
        }}>
          <div>컴포넌트 타입 오류</div>
          <div>예상: function, 실제: {typeof Component}</div>
        </div>
      );
    }
    

    // Render the loaded component
    return <Component {...props} />;
  };
}

// Export all functions
export default {
  registerExternalLayout,
  unregisterExternalLayout,
  getExternalLayouts,
  isExternalLayout,
  loadExternalLayout,
  createExternalLayoutWrapper
};